import { cx } from '@flowus/common/cx';
import { useFinalValue } from '@flowus/common/hooks/react-utils';
import { useLocalStorageState } from 'ahooks';
import { createMemoryHistory } from 'history';
import type { FC } from 'react';
import { useEffect, useLayoutEffect } from 'react';
import { Router, useHistory } from 'react-router-dom';
import { DragHandle } from 'src/common/components/drag-handle';
import { NextModalProvider } from 'src/common/components/next-modal';
import { LocalStorageKey } from 'src/const/local-storage-state-key';
import { useIsMobileSize } from 'src/services/app/hook';
import { parsePageId } from 'src/utils/getPageId';
import { qs } from 'src/utils/querystring';
import { IsInRightContext, fixLocation, parseRightPageId } from 'src/utils/right-utils';

interface RightPageProps {
  uuid: string;
  rightDivRef: React.RefObject<HTMLDivElement>;
}
export const RightPage: FC<RightPageProps> = (props) => {
  const { uuid, rightDivRef } = props;
  const [rightWidth, setRightWidth] = useLocalStorageState(LocalStorageKey.RIGHT_PAGE_WIDTH, {
    defaultValue: '50%',
  });
  const isMobileSize = useIsMobileSize();
  const rightHistory = useFinalValue(() => createMemoryHistory());
  const history = useHistory();

  useLayoutEffect(() => {
    rightHistory.replace({
      pathname: `/${uuid}`,
      search: '',
      hash: location.hash,
    });
  }, [uuid, rightHistory]);

  useEffect(() => {
    return rightHistory.listen((location0, action) => {
      const location = fixLocation(location0);
      const outerRightPageId = parseRightPageId(history.location.search);
      const outerPageId = parsePageId(history.location.pathname);
      const innerRightPageId = parsePageId(location.pathname);

      if (outerRightPageId === innerRightPageId) {
        return;
      }

      if (innerRightPageId === outerPageId) {
        history.replace({
          ...history.location,
          search: '',
        });
        return;
      }

      if (action === 'PUSH') {
        history.push({
          ...history.location,
          search: qs.stringify({
            'right-page-id': innerRightPageId,
          }),
          hash: location.hash,
        });
      }
    });
  }, [history, rightHistory]);

  return (
    <div
      ref={rightDivRef}
      style={{ width: rightWidth }}
      className={cx(
        'right-panel animate__animated animate__fadeIn min-h-0 min-w-[100px] z-50',
        isMobileSize && 'hidden'
      )}
    >
      <div
        className="w-full relative flex flex-col h-full z-50 bg-white2"
        style={{ filter: 'drop-shadow(4px 0 7px rgba(0, 0, 0, 0.3))' }}
      >
        <Router history={rightHistory}>
          <IsInRightContext.Provider value={history}>
            <NextModalProvider>{props.children}</NextModalProvider>
          </IsInRightContext.Provider>
        </Router>
        <DragHandle
          onDragStart={(info) => {
            if (!rightDivRef.current) return;
            const { width } = rightDivRef.current.getBoundingClientRect();
            info.dragStartWidth = width;
          }}
          onDragMove={(info) => {
            requestAnimationFrame(() => {
              if (!rightDivRef.current?.parentElement) return;
              const pWidth = rightDivRef.current.parentElement.getBoundingClientRect().width;
              const width = (info.dragStartWidth as number) - (info.x - info.startX);
              setRightWidth(`${(width / pWidth) * 100}%`);
            });
          }}
        >
          {({ ref }) => (
            <div
              ref={ref as any}
              className={'absolute top-0 bottom-0 left-0 w-3.5 cursor-[col-resize] flex z-50 group'}
              onDoubleClick={() => {
                setRightWidth('50%');
              }}
            >
              <span className="w-px group-hover:bg-grey6 h-full" />
            </div>
          )}
        </DragHandle>
      </div>
    </div>
  );
};

export const RightPageOut = () => {
  // const [rightWidth] = useLocalStorageState(LocalStorageKey.RIGHT_PAGE_WIDTH, {
  //   defaultValue: '50%',
  // });

  return <></>;
};
